import { Fragment, FunctionComponent } from "react";
import { Box, Card, Typography } from "@mui/material";
import styles from "./cardItem.module.css";
import { Document, UserFeedback } from "../../types";
import { Categories, LabelOutputType } from "../../context/ConfigContext/types";
import CardHead from "./CardHead";
import CardTitle from "./CardTitle";
import RelevancyPrompt from "./RelevancyPrompt";
import { usePanelContext } from "../../context/PanelContext";
import ToggleCardView, { CardViewOption } from "./ToggleCardView";

import { colorGrayLight, colorLime } from "../../styles/theme";
import DocumentText from "./DocumentText";
import { useDocuments } from "../../context/DocumentContext";
import { useChat } from "../../context/ChatContext";
import { useConfig } from "../../context/ConfigContext";
import { useClientAccessContext } from "../../context/ClientAccessContext";
import {
	isTranslatable,
	useTranslatedText,
	useTranslationContext,
} from "../../context/TranslationContext";
import { getFullDocumentText } from "../../services/fetch";
import { copyTextToClipboard } from "../../utils";
import { EventName, useEventTracking } from "../../context/EventTrackingContext";

interface CardItemProps {
	item: Document;
	categories: Categories;
	numericLabels: Categories;
	onFeedbackUpdate?: (userFeedback: UserFeedback) => void;
	onFeedbackDelete?: (labelId: number, documentId: number) => void;
}

const HIGHLIGHT_LABELS = {
	SuccessStory: { prediction: "SuccessStory", text: "Success story!" },
	Prospect: { prediction: "Prospect", text: "Potential prospect" },
};

export const getDocumentsRelevancy = (item) => {
	const relevantLabel = item.labels?.find((l) => l.labelName === "Relevancy");
	const relevancyValue = relevantLabel?.prediction?.value || 0;
	return +(relevancyValue * 100).toFixed(0);
};

const CardItem: FunctionComponent<CardItemProps> = ({ item }) => {
	const {
		addDocumentToBasket,
		removeDocumentFromBasket,
		documentsInBasket,
		handleUserFeedback,
		handleUserFeedbackDelete,
	} = useDocuments();
	const { numericLabels } = useConfig();
	const { cardView, toggleCardView } = usePanelContext();
	const { selectedClient } = useClientAccessContext();
	const { trackEvent } = useEventTracking();
	const { isSelectionActive, isDocumentSelected, toggleDocumentSelection } =
		useChat();
	const { isTranslateOn, toggleTranslate } = useTranslationContext();
	const { text: displayedTitle, loading: titleLoading } = useTranslatedText(
		item.id,
		item.title
	);
	const { text: displayedPreview, loading: previewLoading } = useTranslatedText(
		item.id,
		item.previewText
	);
	const relevancy = getDocumentsRelevancy(item);

	const handleCopyFullText = async () => {
		const response = await getFullDocumentText(
			item.id,
			selectedClient.clientName
		);
		if (!response?.isSuccess) {
			throw new Error("Could not fetch the full article text");
		}
		await copyTextToClipboard(response.result);
	};

	const relevancyLabel = item.labels?.find(
		(i) => i.labelType === LabelOutputType.RELEVANCY
	);

	const handleRelevancyChange = (val) => {
		if (val === null) {
			handleUserFeedbackDelete(relevancyLabel.labelId, item.id);
		} else {
			const feedback: UserFeedback = {
				documentId: item.id,
				labelName: relevancyLabel.labelName,
				userId: null,
				labelValue: numericLabels["Relevancy"][0].value,
				possibleLabelValueId: numericLabels["Relevancy"][0].id,
				value: val,
			};
			handleUserFeedback(feedback);
		}
	};
	const allCategoryLabels = item?.labels?.filter(
		(i) => i.labelType == LabelOutputType.CATEGORY
	);
	const categoryLabels = allCategoryLabels?.filter(
		(i) => !HIGHLIGHT_LABELS[i.labelName]
	);
	const highlights = allCategoryLabels
		?.filter(
			(i) =>
				HIGHLIGHT_LABELS[i.labelName] &&
				i.prediction?.labelValue === HIGHLIGHT_LABELS[i.labelName].prediction
		)
		.map((i) => ({
			labelId: i.labelId,
			text: HIGHLIGHT_LABELS[i.labelName].text,
		}));
	const currentView =
		cardView?.documentId === item.id ? cardView.view : CardViewOption.SUMMARY;
	const isChatSelected = isDocumentSelected(item.id);

	return (
		<Card key={item.id} className={styles.card}>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					flex: "1 1 auto",
					minHeight: 0,
				}}
			>
				<CardHead
					score={relevancy}
					isInBasket={documentsInBasket.some((d) => d.id === item.id)}
					onAddToBasket={() => addDocumentToBasket(item.id)}
					onCopyFullText={handleCopyFullText}
					translateOn={isTranslateOn(item.id)}
					onToggleTranslate={
						isTranslatable(item.language)
							? () => toggleTranslate(item.id)
							: undefined
					}
					translateLoading={titleLoading || previewLoading}
					showChatSelect={isSelectionActive}
					chatSelected={isChatSelected}
					onToggleChatSelect={() =>
						toggleDocumentSelection({ id: item.id, title: item.title })
					}
				/>
				<Box
					sx={{
						minHeight: "389px",
						flex: "1 1 auto",
						display: "flex",
						flexDirection: "column",
					}}
				>
					<Box sx={{ marginBottom: "20px", flexShrink: 0 }}>
						<CardTitle
							title={displayedTitle}
							date={item.rawCreationDatetime}
							link={item.url}
						/>
					</Box>
					{categoryLabels?.length || highlights?.length ? (
						<Box
							sx={{
								display: "flex",
								gap: "5px",
								flexWrap: "wrap",
								marginBottom: "16px",
								flexShrink: 0,
							}}
						>
							{highlights?.map((highlight) => (
								<Box
									key={highlight.labelId}
									sx={{
										backgroundColor: colorLime,
										paddingBlock: "5px",
										paddingInline: "10px",
										borderRadius: "12px",
										display: "flex",
									}}
								>
									<Typography
										sx={{
											whiteSpace: "nowrap",
											fontSize: "12px",
											lineHeight: "16px",
											fontWeight: 600,
											color: "#fff",
										}}
									>
										{highlight.text}
									</Typography>
								</Box>
							))}
							{categoryLabels?.map((categoryLabel) => (
								<Fragment key={categoryLabel.labelId}>
									{categoryLabel?.prediction?.labelValue && (
										<Box
											sx={{
												backgroundColor: colorGrayLight,
												paddingBlock: "5px",
												paddingInline: "10px",
												borderRadius: "12px",
												display: "flex",
											}}
										>
											<Typography
												sx={{
													whiteSpace: "nowrap",
													fontSize: "12px",
													lineHeight: "16px",
												}}
											>
												{categoryLabel.prediction.labelValue}
											</Typography>
										</Box>
									)}
								</Fragment>
							))}
						</Box>
					) : null}
					<Box
						sx={{
							flex: "1 1 auto",
							minHeight: "90px",
							maxHeight: "150px",
							marginBottom: "12px",
						}}
					>
						<DocumentText text={displayedPreview} maxHeight="100%" />
					</Box>
					{currentView === CardViewOption.SUMMARY && relevancyLabel && (
						<Box sx={{ marginTop: "auto", flexShrink: 0 }}>
							<RelevancyPrompt
								selectedValue={relevancyLabel?.userFeedback?.value ?? null}
								onChange={handleRelevancyChange}
							/>
						</Box>
					)}
				</Box>
			</Box>
			<Box
				sx={{
					width: "100%",
					height: "34px",
					display: "flex",
					justifyContent: "center",
					marginTop: "20px",
				}}
			>
				<ToggleCardView
					currentView={currentView}
					onViewChange={(view) => {
						if (view !== CardViewOption.SUMMARY) {
							trackEvent(EventName.ARTICLE_OPENED, {
								documentId: item.id,
								properties: {
									title: item.title,
									view,
								},
							});
						}
						toggleCardView(item.id, view);
					}}
				/>
			</Box>
		</Card>
	);
};

export default CardItem;
